// Hlídání nové verze aplikace (PWA, docs/09 §10). Service worker stáhne novou verzi na pozadí, ale
// **nenasadí ji sám** (`registerType: 'prompt'` ve vite.config.ts) — jinak by uživateli zmizela
// rozpracovaná práce. Aplikace o nové verzi řekne pruhem „Je k dispozici nová verze aplikace.“
// a načte ji, až uživatel klikne Obnovit (`PruhNoveVerze.tsx`).
//
// Proč to vzniklo: 22. 9. 2026 měl zadavatel aplikaci otevřenou od rána a nová karta „Zkušební
// platba 1 Kč“ mu chyběla — o nové verzi se dozvěděl jen náhodným obnovením stránky.
//
// Rozhodnutí zadavatele 23. 9. 2026 (05/410, 413, 414), postavené 24. 9. 2026:
//   - zavřený pruh se připomene i **sám po 2 hodinách** (`PRIPOMENUTI_ZAVRENEHO_MS`) vedle dnešního
//     návratu na Domů a další nalezené verze — kdo sedí půl dne v jedné sekci, se k nové verzi
//     dostane týž den (05/410);
//   - **přihlašovací stránka** novou verzi nasadí sama a bez ptaní (`useSamaNovaVerze()`) — není tam
//     co ztratit; veřejné odkazy pro zákazníky (`/podpis/…`, `/podklady/…`) zůstávají beze změny,
//     aby se nikomu nesmazal rozepsaný podpis (05/413);
//   - **kritické nasazení** (opravy kolem peněz a zákonné evidence): nasazená verze se označí
//     příznakem v souboru `/verze.json` vedle buildu (zapisuje `infra/skripty/nasadit-test.sh
//     --kriticka`); aplikace si ho načte spolu s kontrolou verze a po varování a odpočtu
//     (`ODPOCET_KRITICKE_MS`) se načte sama (05/414).
//
// Modul drží stav mimo React (service worker ohlásí verzi kdykoli, i když zrovna žádná komponenta
// neběží); komponenty se přihlásí přes `useNovaVerze()`.

import { useEffect, useRef, useState } from 'react';
import { povolitOdchodBezVarovani } from '../formulare/rozepsano';

export interface StavVerze {
  /** Nová verze je stažená a čeká v prohlížeči (service worker ji drží připravenou). */
  kDispozici: boolean;
  /** Uživatel pruh zavřel — připomene se při další nalezené verzi, po návratu na Domů nebo po 2 hodinách. */
  zavreno: boolean;
  /**
   * Nasazená verze je označená jako **kritická** (oprava kolem peněz nebo zákonné evidence, 05/414):
   * aplikace po varování a odpočtu načte novou verzi sama. Příznak je v `/verze.json` vedle buildu.
   */
  kriticka: boolean;
  /** Jak dlouhý je odpočet u kritického nasazení (ms) — výchozí `ODPOCET_KRITICKE_MS`. */
  odpocetMs: number;
}

/** Jak často se aplikace ptá, jestli není nová verze (ms). `sw.js` je malý a server na něj vrací 304. */
export const INTERVAL_KONTROLY_MS = 5 * 60_000;
/** Nejkratší pauza mezi kontrolami při návratu k otevřené aplikaci (ms) — ať přepínání oken nezdržuje. */
export const PAUZA_PRI_NAVRATU_MS = 60_000;
/** Pojistka: když service worker stránku nepřevezme, načte se sama (ms). */
export const POJISTKA_NACTENI_MS = 3_000;
/**
 * Za jak dlouho se zavřený pruh připomene sám (ms) — rozhodnutí zadavatele 23. 9. 2026 (05/410):
 * 2 hodiny. Připomene se tím, že se pruh zase ukáže (žádný dialog), takže nikomu neskočí do psaní.
 */
export const PRIPOMENUTI_ZAVRENEHO_MS = 2 * 60 * 60_000;
/** Odpočet u kritického nasazení, než se aplikace načte sama (ms) — 05/414, výchozí minuta. */
export const ODPOCET_KRITICKE_MS = 60_000;
/** Soubor s popisem nasazené verze vedle buildu (zapisuje `infra/skripty/nasadit-test.sh`). */
export const SOUBOR_NASAZENI = '/verze.json';

/** Co je v `/verze.json` — nasazená verze a jestli je nasazení kritické (05/414). */
export interface InfoNasazeni {
  /** Verze nasazená na serveru (`git describe`, stejná hodnota jako `APP_VERZE`). */
  verze: string;
  /** Kritická oprava: aplikace se po varování a odpočtu načte sama. */
  kriticka: boolean;
  /** Kdy se nasazovalo (ISO 8601, jen pro ladění). */
  cas?: string;
  /** Jiný odpočet než výchozí minuta (s) — pro výjimky a pro e2e test. */
  odpocet_s?: number;
}

/** Volby registrace service workeru — výřez `RegisterSWOptions` z vite-plugin-pwa, co používáme. */
export interface VolbyRegistrace {
  immediate?: boolean;
  onNeedRefresh?: () => void;
  onOfflineReady?: () => void;
  onRegisteredSW?: (url: string, registrace: ServiceWorkerRegistration | undefined) => void;
  onRegisterError?: (chyba: unknown) => void;
}

/** `registerSW` z `virtual:pwa-register`; testy podvrhnou vlastní. */
export type Registrator = (volby: VolbyRegistrace) => (reload?: boolean) => Promise<void>;

type Posluchac = (stav: StavVerze) => void;

const VYCHOZI_STAV: StavVerze = {
  kDispozici: false,
  zavreno: false,
  kriticka: false,
  odpocetMs: ODPOCET_KRITICKE_MS,
};

let stav: StavVerze = VYCHOZI_STAV;
const posluchaci = new Set<Posluchac>();
/** Co vrátil `registerSW` — zavolání nasadí čekající verzi (skipWaiting) a stránku načte znovu. */
let nasadit: ((reload?: boolean) => Promise<void>) | null = null;
/** Časovač připomenutí zavřeného pruhu (05/410); běží vždy nejvýš jeden. */
let casovacPripomenuti: number | undefined;

function oznam(zmena: Partial<StavVerze>): void {
  stav = { ...stav, ...zmena };
  for (const posluchac of posluchaci) posluchac(stav);
}

function zrusitPripomenuti(): void {
  if (casovacPripomenuti === undefined) return;
  window.clearTimeout(casovacPripomenuti);
  casovacPripomenuti = undefined;
}

/** Aktuální stav (pro kód mimo React). */
export function stavVerze(): StavVerze {
  return stav;
}

/** Přihlásí posluchače stavu nové verze; vrací odhlášení. */
export function sledujVerzi(posluchac: Posluchac): () => void {
  posluchaci.add(posluchac);
  return () => {
    posluchaci.delete(posluchac);
  };
}

/** Stav nové verze v komponentě. */
export function useNovaVerze(): StavVerze {
  const [aktualni, nastav] = useState<StavVerze>(stavVerze);
  useEffect(() => sledujVerzi(nastav), []);
  return aktualni;
}

/**
 * Service worker ohlásil novou verzi (`onNeedRefresh`). Zavřený pruh se tím vrátí: další nalezená
 * verze znamená, že mezitím přibylo něco dalšího, a uživatel o tom má vědět.
 */
export function novaVerzeKDispozici(pouzitAktualizaci: (reload?: boolean) => Promise<void>): void {
  nasadit = pouzitAktualizaci;
  zrusitPripomenuti();
  oznam({ kDispozici: true, zavreno: false });
}

/**
 * Nasazení je označené jako kritické (05/414). Pruh se vrátí, i když ho uživatel zavřel, a přepne se
 * do odpočtu — tohle je ta výjimka, kdy aplikace rozhodne za uživatele.
 */
export function kritickaVerzeKDispozici(odpocetMs = ODPOCET_KRITICKE_MS): void {
  if (stav.kriticka) return;
  zrusitPripomenuti();
  oznam({ kriticka: true, zavreno: false, odpocetMs });
}

/**
 * Uživatel pruh zavřel (křížek) — práce má přednost, připomeneme se později: po návratu na Domů,
 * při další nalezené verzi, nebo sám po 2 hodinách (05/410). Časovač se nastaví jen tady, takže se
 * připomínka nikdy neobjeví dvakrát za sebou bez toho, aby ji uživatel mezitím zase zavřel.
 */
export function zavritPruhVerze(): void {
  // Kritické nasazení zavřít nejde — odpočet běží dál (05/414).
  if (!stav.kDispozici || stav.zavreno || stav.kriticka) return;
  oznam({ zavreno: true });
  zrusitPripomenuti();
  casovacPripomenuti = window.setTimeout(() => {
    casovacPripomenuti = undefined;
    pripomenoutVerzi();
  }, PRIPOMENUTI_ZAVRENEHO_MS);
}

/** Připomenutí zavřeného pruhu (návrat na Domů — tam nic rozepsaného není — nebo po 2 hodinách). */
export function pripomenoutVerzi(): void {
  if (!stav.kDispozici || !stav.zavreno) return;
  zrusitPripomenuti();
  oznam({ zavreno: false });
}

/**
 * Nasadí čekající verzi a načte stránku znovu. Samotné načtení dělá `registerSW` (po převzetí
 * stránky novým service workerem); pojistka ho udělá i tehdy, když se service worker neozve —
 * uživatel, který klikl Obnovit, nesmí zůstat viset na staré verzi. Nativní varování prohlížeče
 * o rozepsaných formulářích se přeskočí: na tohle načtení už se aplikace zeptala sama (05/412).
 */
export async function pouzitNovouVerzi(): Promise<void> {
  povolitOdchodBezVarovani();
  const pouzit = nasadit;
  window.setTimeout(() => window.location.reload(), POJISTKA_NACTENI_MS);
  if (!pouzit) {
    window.location.reload();
    return;
  }
  await pouzit(true);
}

/**
 * Přihlašovací stránka novou verzi nasadí sama a bez ptaní (05/413) — není tam co ztratit. Volá se
 * **jen** na `/prihlaseni`; veřejné odkazy pro zákazníky (`/podpis/…`, `/podklady/…`) hook nepoužívají,
 * protože obnovení uprostřed podpisu by smazalo rozepsaný podpis.
 */
export function useSamaNovaVerze(): void {
  const { kDispozici } = useNovaVerze();
  const uzNasazeno = useRef(false);

  useEffect(() => {
    if (!kDispozici || uzNasazeno.current) return;
    uzNasazeno.current = true;
    void pouzitNovouVerzi();
  }, [kDispozici]);
}

/**
 * Načte `/verze.json` vedle buildu (05/414). Soubor nemusí existovat (vývoj, starší nasazení) —
 * to není chyba, jen se o kritickém nasazení nic neví.
 */
export async function nacistInfoNasazeni(): Promise<InfoNasazeni | null> {
  try {
    const odpoved = await fetch(SOUBOR_NASAZENI, { cache: 'no-store' });
    if (!odpoved.ok) return null;
    const data = (await odpoved.json()) as Partial<InfoNasazeni>;
    if (typeof data.verze !== 'string') return null;
    return {
      verze: data.verze,
      kriticka: data.kriticka === true,
      cas: typeof data.cas === 'string' ? data.cas : undefined,
      odpocet_s:
        typeof data.odpocet_s === 'number' && data.odpocet_s > 0 ? data.odpocet_s : undefined,
    };
  } catch {
    // Bez signálu nebo soubor chybí — kritické nasazení se pozná při další kontrole.
    return null;
  }
}

export interface VolbySledovani {
  /** Vlastní registrace (testy); výchozí je `registerSW` z `virtual:pwa-register`. */
  registrator?: Registrator;
  /** Jak často kontrolovat novou verzi (ms). */
  intervalMs?: number;
  /** Vlastní čtení `/verze.json` (testy). */
  nacistNasazeni?: () => Promise<InfoNasazeni | null>;
  /** Verze, na které aplikace běží (testy); výchozí je `__APP_VERZE__` z buildu. */
  verzeAplikace?: string;
}

/**
 * Zaregistruje service worker a hlídá novou verzi i bez obnovení stránky: každých pár minut
 * `registration.update()` (jen když je aplikace vidět a je signál) a hned po návratu k aplikaci,
 * když byla poslední kontrola dávno. Při každé kontrole se přečte i `/verze.json` — kvůli příznaku
 * kritického nasazení (05/414). Vrací zastavení (testy, úklid).
 */
export function spustSledovaniVerze(volby: VolbySledovani = {}): () => void {
  const intervalMs = volby.intervalMs ?? INTERVAL_KONTROLY_MS;
  const nacistNasazeni = volby.nacistNasazeni ?? nacistInfoNasazeni;
  const verzeAplikace =
    volby.verzeAplikace ?? (typeof __APP_VERZE__ === 'string' ? __APP_VERZE__ : '');
  let registrace: ServiceWorkerRegistration | undefined;
  let casovac: number | undefined;
  let posledniKontrola = 0;
  let zastaveno = false;

  /** Kritické nasazení: příznak platí jen tehdy, když na serveru leží jiná verze, než na které běžíme. */
  const zkontrolovatNasazeni = async (): Promise<void> => {
    const info = await nacistNasazeni();
    if (zastaveno || !info) return;
    if (!info.kriticka || info.verze === verzeAplikace) return;
    kritickaVerzeKDispozici(info.odpocet_s ? info.odpocet_s * 1_000 : ODPOCET_KRITICKE_MS);
  };

  const zkontrolovat = async (): Promise<void> => {
    if (zastaveno || !registrace) return;
    // Šetříme provoz tabletu: skrytá záložka ani tablet bez signálu se serveru neptají.
    if (document.visibilityState !== 'visible') return;
    if (navigator.onLine === false) return;
    posledniKontrola = Date.now();
    try {
      await registrace.update();
    } catch {
      // Bez signálu nebo nedostupný server — zkusí se při další kontrole.
    }
    await zkontrolovatNasazeni();
  };

  const naNavrat = (): void => {
    if (document.visibilityState !== 'visible') return;
    if (Date.now() - posledniKontrola < PAUZA_PRI_NAVRATU_MS) return;
    void zkontrolovat();
  };

  const zaregistrovat = (registrator: Registrator): void => {
    if (zastaveno) return;
    const aktualizovat = registrator({
      immediate: true,
      onNeedRefresh: () => novaVerzeKDispozici(aktualizovat),
      onRegisteredSW: (_url, r) => {
        registrace = r;
        posledniKontrola = Date.now();
        if (!r || zastaveno) return;
        casovac = window.setInterval(() => void zkontrolovat(), intervalMs);
      },
      onRegisterError: (chyba) => {
        // Bez service workeru aplikace funguje dál (jen bez offline režimu a bez pruhu verze).
        console.warn('Service worker se nepodařilo zaregistrovat.', chyba);
      },
    });
  };

  if (volby.registrator) zaregistrovat(volby.registrator);
  else void import('virtual:pwa-register').then(({ registerSW }) => zaregistrovat(registerSW));

  // Kritické nasazení se pozná i hned po otevření aplikace, ne až za pět minut.
  void zkontrolovatNasazeni();

  document.addEventListener('visibilitychange', naNavrat);
  return () => {
    zastaveno = true;
    if (casovac !== undefined) window.clearInterval(casovac);
    document.removeEventListener('visibilitychange', naNavrat);
  };
}

/** Jen pro testy: vrátí modul do výchozího stavu (stav verze je globální, testy se nesmí ovlivňovat). */
export function resetVerzeProTesty(): void {
  zrusitPripomenuti();
  stav = VYCHOZI_STAV;
  nasadit = null;
  posluchaci.clear();
}
